import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showVerify, setShowVerify] = useState(false);
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Welcome back!');
    } catch (err) {
      console.error('Login form error:', err);
      const message = err.message || 'Login failed. Please try again.';
      setError(message);
      setShowVerify(message.toLowerCase().includes('not verified'));
      toast.error(message);
      setLoading(false);
      return false;
    }
  };

  // Save invite token if present
  if (inviteToken) {
    localStorage.setItem('pendingInviteToken', inviteToken);
  }

  return (
    <div className="min-h-screen flex items-center justify-center gradient-soft p-4">
      <Card className="w-full max-w-md p-8 shadow-strong">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold gradient-text mb-2">Shaadi Buddy</h1>
          <p className="text-muted-foreground">Welcome back! Sign in to continue</p>
        </div>

        {inviteToken && (
          <Alert className="mb-6 border-primary bg-primary/10">
            <AlertDescription className="text-sm">
              You'll be added to the organization after logging in
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert className="mb-6 border-destructive bg-destructive/10">
            <AlertDescription className="text-destructive">{error}</AlertDescription>
          </Alert>
        )}
        {showVerify && (
          <div className="mb-4 text-sm text-center">
            <Link
              to={`/verify-email?email=${encodeURIComponent(email)}`}
              className="text-primary hover:underline font-medium"
            >
              Verify your email
            </Link>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <div className="relative mt-1.5">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="pl-10"
                required
                disabled={loading}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="password">Password</Label>
            <div className="relative mt-1.5">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="pl-10"
                required
                disabled={loading}
              />
            </div>
          </div>

          <Button type="submit" className="w-full gradient-primary" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm space-y-2">
          <p className="text-muted-foreground">
            Don't have an account?{' '}
            <Link 
              to={inviteToken ? `/signup?invite=${inviteToken}` : '/signup'} 
              className="text-primary hover:underline font-medium"
            >
              Sign up
            </Link>
          </p>
          <p className="text-muted-foreground">
            <Link 
              to="/forgot-password" 
              className="text-primary hover:underline font-medium"
            >
              Forgot your password?
            </Link>
          </p>
        </div>

      </Card>
    </div>
  );
}